import { t } from 'i18next';
import { ComponentType, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { safeParseInt } from '../../../../../../shared/common/utils';
import { areFieldsDifferent } from '../../../../../../shared/frontend/libs/utils';
import { createActionSetError, TError } from '../../../../../../shared/frontend/scripts/actions/errorActions';
import { useAllRaids, useSiteConfig } from '../../../../../../shared/frontend/scripts/selectors/global';
import { RouteComponentProps } from '../../../../../../shared/common/types/common';
import {
  createActionGetRaidRankings,
  createActionShowRaidRankingsSpinner,
  setActionGetRaidRankingsError
} from '../../actions/getRaidRankings';
import frontendLog from '../../libs/frontendLog';
import { TApplicationState, TRaidRankingsClientState } from '../../libs/store';
import { apiServiceAsync, TGetRaidInstanceRankingsParams } from '../../services/apiService';

type TPropsFromState = Partial<TRaidRankingsClientState> & { siteConfig: any } & { isError?: any };

type TPropsThatDispatch = {
  setRaidRankings(instanceId: string, actionData: any): void;
  setRaidRankingsError(instanceId: string, error: any): void;
  showError(actionData: TError): void;
  showLoadingSpinner(instanceId: string, show: boolean): void;
};

type TRaidRankingsProps = TPropsFromState &
  TPropsThatDispatch &
  RouteComponentProps<any, any> & { component: ComponentType<any>; controlledDifficulty?: boolean };

/**
 * RaidRankingsLoader
 */
const RaidRankingsLoader = ({ component: Component, ...props }: TRaidRankingsProps) => {
  const [difficulty, setDifficulty] = useState(props.difficulty);
  const dispatch = useDispatch();
  const siteConfig = useSiteConfig();
  const allRaids = useAllRaids();
  const raidRankings = useSelector((state: TApplicationState) => state.raidRankings);
  const raidInstanceId = props.instanceId || 'default';

  useEffect(() => {
    setDifficulty(props.difficulty);
  }, [props.difficulty]);

  const currentRaidInstanceState = useMemo(() => {
    if (props.cacheRaid && props.instanceId) {
      const currentInstance = raidRankings!.instances[props.instanceId] || {};
      const defaultInstance = raidRankings!.instances.default || {};
      return {
        ...defaultInstance,
        ...currentInstance
      };
    }

    return raidRankings!.instances[raidInstanceId] || {};
  }, [raidRankings, props.cacheRaid, props.instanceId, raidInstanceId]);

  const setRaidRankings = (instanceId: string, rankings: any) => {
    dispatch(createActionGetRaidRankings(instanceId, rankings));
  };

  const setRaidRankingsError = (instanceId: string, error: any) => {
    dispatch(setActionGetRaidRankingsError(instanceId, error));
  };

  const showError = (actionData: TError) => {
    dispatch(createActionSetError(actionData));
  };

  const showLoadingSpinner = (instanceId: string, show: boolean) => {
    dispatch(createActionShowRaidRankingsSpinner(instanceId, show));
  };

  const loadParams: TGetRaidInstanceRankingsParams = {
    difficulty,
    raid: props.raid,
    region: props.region,
    realm: props.realm || 'all',
    page: safeParseInt(props.page, 0),
    faction: props.faction || '',
    guildsFilter: props.guildsFilter,
    partition: props.partition,
    recent: props.recent || false,
    limit: props.limit || 0
  };

  useEffect(() => {
    const checkFields = ['raid', 'difficulty', 'region', 'realm', 'page', 'faction', 'recent', 'guildsFilter', 'limit', 'partition'];
    const contentChanged =
      !currentRaidInstanceState.ui || areFieldsDifferent(checkFields, loadParams, currentRaidInstanceState.ui);

    const fetchHandler = async () => {
      showLoadingSpinner(raidInstanceId, true);
      try {
        const result = await apiServiceAsync.getRaidInstanceRankings(loadParams);

        if (!result) {
          if (!props.ignoreErrors) {
            showError({
              title: t('common:errors.something-went-wrong'),
              body: t('common:errors.unable-to-load-data-reload')
            });
          }
          setRaidRankingsError(raidInstanceId, {
            title: t('common:errors.something-went-wrong'),
            body: t('common:errors.unable-to-load-data-reload')
          });
          frontendLog.error('getRaidInstanceRankings got empty results');
          showLoadingSpinner(raidInstanceId, false);
          return;
        }

        setRaidRankings(raidInstanceId, { ...result.raidRankings });
        showLoadingSpinner(raidInstanceId, false);
      } catch (err) {
        if (!props.ignoreErrors) {
          showError({
            title: t('common:errors.something-went-wrong'),
            body: err.toString()
          });
        }
        setRaidRankingsError(raidInstanceId, err);
        frontendLog.error({ err }, 'getRaidInstanceRankings failed');
        showLoadingSpinner(raidInstanceId, false);
      }
    };

    if (
      !currentRaidInstanceState.uiLoading &&
      (!currentRaidInstanceState.rankedGuilds || contentChanged) &&
      !currentRaidInstanceState.isError
    ) {
      fetchHandler();
    }
  }, [JSON.stringify(loadParams), JSON.stringify(currentRaidInstanceState)]);

  return (
    <Component
      {...props}
      {...currentRaidInstanceState}
      difficulty={difficulty}
      setDifficulty={props.controlledDifficulty ? setDifficulty : undefined}
      setRaidRankings={setRaidRankings}
      setRaidRankingsError={setRaidRankingsError}
      showError={showError}
      showLoadingSpinner={showLoadingSpinner}
      siteConfig={siteConfig}
      allRaids={allRaids}
    />
  );
};

export default RaidRankingsLoader;
